import { applyI18n, t } from '../i18n';

const NAV_SETTLE_MS = 250;

export async function renderPptxPreview(
  content: HTMLElement,
  blob: Blob,
): Promise<{ dispose?: () => void } | false> {
  const wrap = document.createElement('div');
  wrap.className = 'file-preview-pptx';
  const loading = document.createElement('p');
  loading.className = 'muted';
  loading.textContent = t('status.loading');
  wrap.append(loading);
  content.append(wrap);

  try {
    const { PptxViewer, RECOMMENDED_ZIP_LIMITS } = await import('@aiden0z/pptx-renderer');

    const slides = document.createElement('div');
    slides.className = 'file-preview-pptx-slides';
    wrap.replaceChildren(slides);

    let displayedIndex = 0;
    let pendingNav: number | null = null;
    let navGen = 0;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let syncNav = (_index: number) => {};

    const clearPending = (gen?: number) => {
      if (gen !== undefined && gen !== navGen) return;
      pendingNav = null;
      if (settleTimer !== null) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
    };

    const viewer = await PptxViewer.open(await blob.arrayBuffer(), slides, {
      zipLimits: RECOMMENDED_ZIP_LIMITS,
      lazySlides: true,
      lazyMedia: true,
      pdfjs: false,
      fitMode: 'contain',
      scrollContainer: slides,
      onSlideChange: (index) => {
        // Ignore IntersectionObserver chatter while a button jump is scrolling.
        if (pendingNav !== null) return;
        displayedIndex = index;
        syncNav(index);
      },
      listOptions: { windowed: true, initialSlides: 4, batchSize: 4 },
    });

    if (viewer.slideCount === 0) {
      viewer.destroy();
      wrap.remove();
      return false;
    }

    const navigator = createSlideNavigator(viewer.slideCount);
    syncNav = navigator.syncNav;
    wrap.prepend(navigator.nav);
    displayedIndex = viewer.currentSlideIndex;
    syncNav(displayedIndex);

    const jumpTo = (index: number) => {
      if (index === displayedIndex && pendingNav === null) return;
      displayedIndex = index;
      pendingNav = index;
      const gen = ++navGen;
      syncNav(index);
      void viewer.goToSlide(index, { behavior: 'instant', block: 'start' }).finally(() => {
        if (settleTimer !== null) clearTimeout(settleTimer);
        settleTimer = setTimeout(() => clearPending(gen), NAV_SETTLE_MS);
      });
    };

    navigator.prev.addEventListener('click', () => {
      jumpTo(Math.max(0, displayedIndex - 1));
    });
    navigator.next.addEventListener('click', () => {
      jumpTo(Math.min(viewer.slideCount - 1, displayedIndex + 1));
    });

    const onScrollEnd = () => clearPending();
    slides.addEventListener('scrollend', onScrollEnd);

    const onLinkClick = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('a')) event.preventDefault();
    };
    slides.addEventListener('click', onLinkClick);

    return {
      dispose: () => {
        clearPending();
        slides.removeEventListener('scrollend', onScrollEnd);
        slides.removeEventListener('click', onLinkClick);
        viewer.destroy();
      },
    };
  } catch {
    wrap.remove();
    return false;
  }
}

function createSlideNavigator(total: number): {
  nav: HTMLElement;
  prev: HTMLButtonElement;
  next: HTMLButtonElement;
  syncNav: (index: number) => void;
} {
  const nav = document.createElement('div');
  nav.className = 'file-preview-pptx-nav';

  const prev = document.createElement('button');
  prev.type = 'button';
  prev.className = 'button ghost file-preview-pptx-nav-btn';
  prev.dataset.pptxNav = 'prev';
  prev.dataset.i18nAriaLabel = 'files.preview.pptx.prev';
  prev.textContent = '<';

  const status = document.createElement('span');
  status.className = 'file-preview-pptx-status';
  status.dataset.pptxStatus = 'true';

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'button ghost file-preview-pptx-nav-btn';
  next.dataset.pptxNav = 'next';
  next.dataset.i18nAriaLabel = 'files.preview.pptx.next';
  next.textContent = '>';

  nav.append(prev, status, next);
  applyI18n(nav);

  const syncNav = (index: number) => {
    const current = Math.min(Math.max(index, 0), Math.max(total - 1, 0)) + 1;
    status.dataset.pptxCurrent = String(current);
    status.dataset.pptxTotal = String(total);
    status.textContent = t('files.preview.pptx.status', { current, total });
    prev.disabled = current <= 1;
    next.disabled = current >= total;
  };

  return { nav, prev, next, syncNav };
}
